import React from 'react';
import { cn } from "../../lib/utils";

interface WeeklyCalendarProps {
  selectedDay: string; // 'monday', 'tuesday', etc.
  onDaySelect: (day: string, dateStr: string) => void;
  daysWithTrends: string[]; // Array of days that have trends
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Mon', fullLabel: 'Monday' },
  { key: 'tuesday', label: 'Tue', fullLabel: 'Tuesday' },
  { key: 'wednesday', label: 'Wed', fullLabel: 'Wednesday' },
  { key: 'thursday', label: 'Thu', fullLabel: 'Thursday' },
  { key: 'friday', label: 'Fri', fullLabel: 'Friday' },
  { key: 'saturday', label: 'Sat', fullLabel: 'Saturday' },
  { key: 'sunday', label: 'Sun', fullLabel: 'Sunday' },
];

export default function WeeklyCalendar({
  selectedDay,
  onDaySelect,
  daysWithTrends,
}: WeeklyCalendarProps): JSX.Element {
  const today = new Date();
  
  // Create a 7-day sliding window centered on today
  const windowDays = [];
  for (let i = -3; i <= 3; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const key = dayKeys[d.getDay()];
    const label = d.toLocaleDateString('en-US', { weekday: 'short' });
    const dayNumber = d.getDate();
    const isToday = i === 0;
    const dateStr = d.toISOString().split('T')[0]; // Use full date string as key for better selection

    windowDays.push({ key, label, dayNumber, isToday, dateStr });
  }

  return (
    <div className="p-6 w-full flex flex-col">
      <div className="flex justify-between gap-2">
        {windowDays.map((day) => {
          // Check if selectedDay (which is a day name from old logic) matches this day's name
          // OR if we should transition selectedDay to be a full date string?
          // For now, let's keep it based on day names but highlight correctly
          const isSelected = selectedDay === day.key;
          const isToday = day.isToday;
          const hasTrends = daysWithTrends.includes(day.key);

          return (
            <button
              key={day.dateStr}
              onClick={() => onDaySelect(day.key, day.dateStr)}
              className={cn(
                "flex-1 flex flex-col items-center justify-center py-3 px-1 rounded-2xl transition-all duration-300 relative group outline-none",
                isSelected 
                  ? "bg-primary text-white shadow-lg shadow-primary/20 scale-105 z-10" 
                  : isToday
                    ? "bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20"
                    : "bg-card/20 border border-transparent text-muted-foreground hover:bg-card/40 hover:text-foreground"
              )}
            >
              <span className={cn(
                "text-[10px] font-black uppercase tracking-widest mb-1",
                isSelected ? "text-white" : isToday ? "text-primary" : "text-muted-foreground group-hover:text-muted-foreground"
              )}>
                {day.label}
              </span>
              <span className={cn(
                "text-lg font-black leading-none",
                isSelected ? "text-white" : isToday ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
              )}>
                {day.dayNumber}
              </span>
              
              {hasTrends && (
                <div className={cn(
                  "absolute top-2 right-2 h-1.5 w-1.5 rounded-full",
                  isSelected ? "bg-white" : "bg-green-500"
                )} />
              )}

              {isSelected && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-1 bg-white rounded-full opacity-50" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
